// ============================================================================
// inventoryLedger.js
//
// Single, authoritative module for all INVOICE-DRIVEN inventory movement.
// It is designed to replace the three uncoordinated deduction paths that exist
// today (NewDocument full-deduct, convertQuote full-deduct, EditDocument
// stale-baseline delta), which together caused items to be deducted multiple
// times for one invoice. Nothing imports this module yet — it is inert until
// wired up in a later step.
//
// ── THE LEDGER ──────────────────────────────────────────────────────────────
// Each invoice carries a record of what has ALREADY been applied to inventory:
//
//     deductedInventory: { [productId]: quantityDeducted }
//
// On every save/convert we:
//   1. aggregate the invoice's CURRENT deductable quantities per product
//      (getDeductableQuantities),
//   2. diff them against the stored ledger (computeInventoryDelta),
//   3. apply ONLY the delta atomically (applyInventoryDelta) — deduct increases,
//      restore decreases/removals,
//   4. persist the current quantities as the new ledger (buildUpdatedLedger).
//
// A no-op save (nothing changed) produces an empty delta, so there is NO
// inventory write and NO confirmation prompt. This is the property the old
// code lacked: its baseline was a client-side snapshot that went stale after
// navigation and was never refreshed after a save, so repeated saves re-applied
// the same delta.
//
// ── WHY increment() ─────────────────────────────────────────────────────────
// The old code read stockQty from the cached products array, computed a new
// absolute value, and wrote it back — a read-modify-write against STALE data,
// racy under concurrent edits, and it silently floored negatives with
// Math.max(0, …) which HID over-deduction. Here we use Firestore's server-side
// increment(): atomic, race-free, and it never invents an absolute value from a
// possibly-stale cache. We deliberately do NOT clamp at zero so genuine
// discrepancies surface as negative stock (with a best-effort warning) instead
// of being silently erased.
//
// ── LEGACY MIGRATION ────────────────────────────────────────────────────────
// Invoices created before this ledger existed have no `deductedInventory` field
// but WERE already deducted by the old code paths. getEffectiveLedger() treats a
// missing ledger as "already fully deducted" (seeded to the current items) so
// the first save under the new system produces a ZERO delta and never
// re-deducts. See that function for the full rationale.
//
// ── PURITY ──────────────────────────────────────────────────────────────────
// No React, no DataContext. `db`, `products`, and `user` are passed in as
// parameters so the module is side-effect-free and testable until called.
// ============================================================================

import { writeBatch, doc, collection, increment } from 'firebase/firestore';

// ── 1) Aggregate current deductable quantities per product ──────────────────
// Sums quantities across ALL line items that share a productId. The old
// EditDocument delta used .find() and silently ignored duplicate-product lines;
// that latent bug is intentionally NOT carried over.
//
// Filtering rules (must match the three existing implementations exactly):
//   - skip items with no productId
//   - skip isNonStockCustom
//   - skip isReturnCredit (goods coming back, never deducted)
//   - skip unless the matching product has trackInventory === true
//   - skip qty <= 0
export function getDeductableQuantities(lineItems, products) {
  const totals = {};
  (lineItems || []).forEach(item => {
    if (!item || !item.productId) return;
    if (item.isNonStockCustom) return;
    if (item.isReturnCredit) return;
    const product = (products || []).find(p => p.id === item.productId);
    if (!product || product.trackInventory !== true) return;
    const qty = parseFloat(item.qty) || 0;
    if (qty <= 0) return;
    totals[item.productId] = (totals[item.productId] || 0) + qty;
  });
  return totals;
}

// ── 2) Diff current quantities against the stored ledger ────────────────────
// For every productId present in EITHER map:
//     delta = current - ledger
// Returns only non-zero deltas. delta > 0 → deduct `delta`; delta < 0 → restore
// `|delta|`.
export function computeInventoryDelta(currentQuantities, ledger) {
  const current = currentQuantities || {};
  const prev = ledger || {};
  const productIds = new Set([...Object.keys(current), ...Object.keys(prev)]);
  const changes = [];
  productIds.forEach(productId => {
    const delta = (current[productId] || 0) - (prev[productId] || 0);
    if (delta !== 0) changes.push({ productId, delta });
  });
  return changes;
}

// ── 3) Enrich a delta for display in the confirmation prompt ────────────────
// Looks up sku/description from the products array, falling back to the
// productId string when the product isn't found.
export function describeDelta(changes, products) {
  return (changes || []).map(change => {
    const product = (products || []).find(p => p.id === change.productId);
    return {
      productId: change.productId,
      sku: product?.sku || change.productId,
      description: product?.description || change.productId,
      delta: change.delta,
      direction: change.delta > 0 ? 'deduct' : 'restore',
      qty: Math.abs(change.delta),
    };
  });
}

// ── 4) Apply the delta to Firestore atomically ──────────────────────────────
// - stockQty is moved with increment() (server-side, atomic, no stale-cache
//   read-modify-write). The stock change is always -delta: deducting a positive
//   delta lowers stock, restoring a negative delta raises it.
// - No zero-clamp: resulting stock may go negative so discrepancies are visible.
//   `warnings` best-effort names any product whose cached stock suggests the
//   result would drop below zero — informational only, it never blocks the write.
// - In the SAME batch, one document per change is written to 'inventoryMovements'
//   as an audit log (signed qty: negative = deduction, positive = restore).
//
// Returns { newLedgerEntries, movements, warnings }. `movements` is the list of
// audit records written; `newLedgerEntries` is the applied changes (per-product
// deltas). The caller persists the authoritative new ledger via
// buildUpdatedLedger(currentQuantities) onto the invoice's deductedInventory.
export async function applyInventoryDelta({ changes, products, docId, docType, docNumber, user, db }) {
  const movements = [];
  const warnings = [];
  if (!changes || changes.length === 0) {
    return { newLedgerEntries: [], movements, warnings };
  }

  try {
    const batch = writeBatch(db);
    const createdAt = new Date().toISOString();
    const userEmail = user?.email || null;

    changes.forEach(change => {
      const product = (products || []).find(p => p.id === change.productId);
      const sku = product?.sku || change.productId;
      const description = product?.description || change.productId;

      // stockQty change: deduct (delta>0) lowers it, restore (delta<0) raises it.
      const stockChange = -change.delta;

      // Best-effort negative-stock warning from the cached value (informational).
      if (change.delta > 0 && product) {
        const cachedStock = parseFloat(product.stockQty) || 0;
        if (cachedStock - change.delta < 0) {
          warnings.push(
            `${sku} — deducting ${change.delta} would drop stock below zero (cached: ${cachedStock}).`
          );
        }
      }

      // Atomic server-side stock movement — no read of the cached absolute value.
      batch.update(doc(db, 'products', change.productId), { stockQty: increment(stockChange) });

      // Audit-log entry (signed qty matches the stock change direction).
      const movementRef = doc(collection(db, 'inventoryMovements'));
      const movement = {
        productId: change.productId,
        sku,
        description,
        qty: stockChange, // negative for deduction, positive for restore
        reason: 'invoice_sync',
        docId,
        docType,
        docNumber,
        userEmail,
        createdAt,
      };
      batch.set(movementRef, movement);
      movements.push(movement);
    });

    await batch.commit();
  } catch (err) {
    throw new Error(`Inventory ledger apply failed for ${docType} ${docNumber || docId}: ${err?.message || err}`);
  }

  return { newLedgerEntries: changes, movements, warnings };
}

// ── 5) Build the ledger to store after a successful apply ───────────────────
// The invoice's new deductedInventory is simply the current quantities map.
// Kept as its own function for clarity and future flexibility.
export function buildUpdatedLedger(currentQuantities) {
  return { ...(currentQuantities || {}) };
}

// ── 6) Resolve the effective ledger for a document (LEGACY MIGRATION) ────────
// CRITICAL for existing invoices.
//
//   - If doc.deductedInventory exists (even an empty {}), it is authoritative —
//     return it as-is. An empty object legitimately means "nothing deducted yet."
//
//   - If it is ABSENT, the invoice predates this ledger. Such invoices were
//     ALREADY deducted by the old NewDocument / convertQuote / EditDocument code
//     paths. If we treated their ledger as empty, the very next save would see a
//     full positive delta and DEDUCT EVERYTHING A SECOND TIME — corrupting
//     inventory (exactly the class of bug this module exists to fix). So we
//     assume "already fully deducted" and seed the ledger to the current
//     deductable quantities. The next delta is then zero and nothing is
//     re-deducted. Assuming already-deducted is the SAFE direction: worst case a
//     later genuine change is diffed against a correct baseline; the alternative
//     (assuming not-deducted) guarantees a double-deduction.
export function getEffectiveLedger(doc, lineItems, products) {
  if (doc && doc.deductedInventory) {
    return doc.deductedInventory;
  }
  return getDeductableQuantities(lineItems, products);
}

// ── 7) Resolve an invoice's inventory status (NEW MANUAL MODEL) ──────────────
// Under the manual model an invoice is either 'pending' (stock not yet deducted)
// or 'deducted' (stock already removed via the Mark-as-Deducted action).
//
//   - An explicit `inventoryStatus` field is AUTHORITATIVE — return it as-is.
//   - Legacy invoices (created before this field existed) have no status. They
//     are inferred as 'deducted' when they ALREADY moved stock under the old
//     auto-deduct model — either the old `inventoryDeducted === true` flag, or a
//     non-empty `deductedInventory` ledger — so their stock is never re-deducted
//     or misreported as pending. Otherwise they resolve to 'pending'.
//   - A null/undefined doc defaults to 'pending' (safe: nothing has been deducted).
export function resolveInventoryStatus(doc) {
  if (!doc) return 'pending';
  if (doc.inventoryStatus === 'pending' || doc.inventoryStatus === 'deducted') {
    return doc.inventoryStatus;
  }
  // Legacy inference: invoices created before the status field existed.
  if (doc.inventoryDeducted === true) return 'deducted';
  if (doc.deductedInventory && typeof doc.deductedInventory === 'object' && Object.keys(doc.deductedInventory).length > 0) {
    return 'deducted';
  }
  return 'pending';
}
